import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent, CheckboxComponent, TextareaComponent } from '@epd/pattern-library';
import { ChatRole } from '@app/enums';

import { ResponseFeedbackOption, ResponseFeedbackPayload } from './response-feedback.interface';
import { FEEDBACK_OPTIONS_CONFIG } from './feedback-options.config';
import { AuthClientService } from '../../auth-client/auth-client.service';
import { StreamingAuthService } from './streaming-auth.service';
import { RealtimeSessionStreamService } from '../../realtime-session-stream/realtime-session-stream.service';
import { FeedbackTrackingService } from './feedback-tracking.service';
import { ConversationMessage } from '../../realtime-session-stream/conversation-message.interface';
import { FeedbackStateManagerService } from '../../realtime-session-stream/feedback-state-manager.service';
import { FeedbackSentiment, FeedbackCategory } from '../../realtime-session-stream/feedback-state.interface';
import * as FeedbackLogic from './feedback.business-logic';

@Component({
    selector: 'craig-streaming-response-feedback',
    templateUrl: './response-feedback.component.html',
    styleUrl: './response-feedback.component.less',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [StreamingAuthService],
    imports: [ReactiveFormsModule, ButtonComponent, CheckboxComponent, TextareaComponent],
    standalone: true
})
export class ResponseFeedbackComponent implements OnInit, OnDestroy {
    @Input() requestId: string = '';
    @Input() feedbackMessage?: ConversationMessage;
    @ViewChild('feedbackContainer') feedbackContainer!: ElementRef;

    readonly feedbackOptions: ResponseFeedbackOption[] = FEEDBACK_OPTIONS_CONFIG();
    selectedFeedbackOption: ResponseFeedbackOption | undefined = undefined;
    feedbackForm!: FormGroup;

    isSubmitting: boolean = false;
    showSuccessMessage: boolean = false;
    showErrorMessage: boolean = false;
    errorMessage: string = '';

    private readonly componentDestroyed$ = new Subject<void>();

    constructor(
        private readonly fb: FormBuilder,
        private readonly authClientService: AuthClientService,
        private readonly authService: StreamingAuthService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly realtimeStreamService: RealtimeSessionStreamService,
        private readonly feedbackTrackingService: FeedbackTrackingService,
        private readonly feedbackStateManager: FeedbackStateManagerService
    ) {}

    ngOnInit(): void {
        this.initForm();
        // console.log(
        //     '🎯 ResponseFeedbackComponent initialized for requestId:',
        //     this.requestId,
        //     'feedbackMessage:',
        //     this.feedbackMessage
        // );
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    get shouldShowFeedbackUI(): boolean {
        // Delegate to business logic
        return FeedbackLogic.shouldShowFeedbackUI(this.feedbackMessage);
    }

    onFeedbackOptionClick(option: ResponseFeedbackOption): void {
        // console.log('🎯 User clicked feedback option:', option.displayName);
        this.selectedFeedbackOption = option;
        this.feedbackForm.reset({ reasons: [], comment: '' });
        this.showSuccessMessage = false;
        this.showErrorMessage = false;

        this.changeDetectorRef.detectChanges();
        this.feedbackContainer.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });
    }

    submitResponseFeedback(): void {
        if (!this.selectedFeedbackOption || this.isSubmitting) {
            return;
        }

        // console.log('🚀 User clicked submit feedback button - using optimistic UI');

        // Show submitting state briefly
        this.isSubmitting = true;
        this.showErrorMessage = false;
        this.changeDetectorRef.detectChanges();

        // Use business logic to prepare data
        const feedbackData = FeedbackLogic.prepareFeedbackStateData(
            this.selectedFeedbackOption.value,
            this.feedbackForm.value.reasons || [],
            this.feedbackForm.value.comment || '',
            this.authService.getUserEmail()
        );

        // OPTIMISTIC UI: After a brief delay, hide component and show thank you
        setTimeout(() => {
            this.isSubmitting = false;

            // Update UI state through state manager - hide prompt and show success
            this.feedbackStateManager.updateUIState({
                showPrompt: false,
                isSubmitting: false,
                showSuccess: true,
                showError: false
            });

            // Mark feedback as submitted with full data in conversation state
            // This will remove the feedback prompt and show a thank you message
            this.realtimeStreamService.markFeedbackSubmitted(this.requestId, feedbackData.sentiment, {
                categories: feedbackData.categories,
                reasons: feedbackData.reasons,
                comment: feedbackData.comment,
                userEmail: feedbackData.userEmail
            });

            // Clear the selected option and form
            this.selectedFeedbackOption = undefined;
            this.feedbackForm.reset();
            this.showSuccessMessage = false;

            // console.log('🎯 Feedback optimistically marked as submitted for requestId:', this.requestId);
            this.changeDetectorRef.detectChanges();
        }, 500); // Brief 500ms delay to show submitting state

        // Send API request in background - don't wait for response
        const payload: ResponseFeedbackPayload = FeedbackLogic.buildFeedbackPayload({
            requestId: this.requestId,
            email: feedbackData.userEmail,
            optionValue: this.selectedFeedbackOption.value,
            reasons: feedbackData.reasons,
            comment: feedbackData.comment
        });

        // Background API call - silent failure as requested
        this.authClientService.sendResponseFeedback(payload).subscribe({
            next: () => {
                // console.log('✅ Background feedback submission successful');
                // UI is already updated optimistically, no need to do anything
            },
            error: (error) => {
                // console.log('❌ Background feedback submission failed (silent):', error);
                // Silent failure - user already sees success state
                // Could optionally log this for monitoring/debugging purposes
            }
        });
    }

    onToggleFeedbackReason(clickedReason: string, isChecked: boolean): void {
        const currentReasons: string[] = this.feedbackForm.controls['reasons'].value || [];
        const updatedReasons = FeedbackLogic.toggleReason(currentReasons, clickedReason, isChecked);
        this.feedbackForm.controls['reasons'].setValue(updatedReasons);
    }

    onCommentChange(event: Event): void {
        if (this.feedbackForm) {
            const commentValue: string = (event.target as HTMLInputElement).value;
            this.feedbackForm.controls['comment'].setValue(commentValue);
        }
    }

    private initForm(): void {
        this.feedbackForm = this.fb.group({
            reasons: new FormControl([]),
            comment: new FormControl('')
        });
    }

    private resetResponseFeedback(): void {
        // Simple reset of local UI state
        this.selectedFeedbackOption = undefined;
        this.showSuccessMessage = false;
        this.showErrorMessage = false;
        this.isSubmitting = false;
        this.feedbackForm.reset();
        this.changeDetectorRef.detectChanges();
    }
}
